import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api, { fmt, fmtQty } from '../../utils/api';

const TABS = ['priceList'];

export default function PricingPage() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin', 'accounting');
  const [tab, setTab] = useState('priceList');
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [priceListData, setPriceListData] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [sort, setSort] = useState({ key: 'sku', dir: 'asc' });
  const [plFilter, setPlFilter] = useState('');
  const [groupAirPrice, setGroupAirPrice] = useState('');
  const [groupOceanPrice, setGroupOceanPrice] = useState('');
  const [locked, setLocked] = useState(true);

  const fetchTab = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (tab === 'orderPricing') {
        const res = await api.get('/pricing');
        setOrderData(res.data);
      } else {
        const res = await api.get('/pricing/price-list');
        setPriceListData(res.data);
      }
    } catch {} finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchTab(); }, [fetchTab]);

  const toggle = (no) => setExpanded(prev => {
    const n = new Set(prev); n.has(no) ? n.delete(no) : n.add(no); return n;
  });

  const saveGroupPrice = async (items) => {
    const air = groupAirPrice ? parseFloat(groupAirPrice) : undefined;
    const ocean = groupOceanPrice ? parseFloat(groupOceanPrice) : undefined;
    if ((!air && air !== 0) && (!ocean && ocean !== 0)) return;
    if (!items.length) return;
    try {
      for (const item of items) {
        await api.put('/pricing/sku-price', { sku: item.sku, airPrice: air, oceanPrice: ocean });
      }
      setGroupAirPrice('');
      setGroupOceanPrice('');
      fetchTab(true);
    } catch {}
  };

  const saveSkuPrice = async (sku, field, value) => {
    if (!value && value !== 0) return;
    try {
      const body = { sku };
      if (field === 'air') body.airPrice = parseFloat(value);
      else body.oceanPrice = parseFloat(value);
      await api.put('/pricing/sku-price', body);
      fetchTab(true);
    } catch {}
  };

  const saveOrderPrice = async (orderNo, sku, cellcomQty, price) => {
    if (!price && price !== 0) return;
    try {
      await api.put('/pricing/order-price', { orderNo, sku, cellcomQty, sellingPrice: parseFloat(price) });
      fetchTab(true);
    } catch {}
  };

  const toggleSort = (key) => setSort(prev => ({
    key,
    dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
  }));

  const sortIcon = (key) => sort.key === key ? (sort.dir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  const sortItems = (items) => {
    if (!items) return [];
    return [...items].sort((a, b) => {
      let va, vb;
      if (sort.key === 'sku') { va = (a.displaySku || a.sku || '').toLowerCase(); vb = (b.displaySku || b.sku || '').toLowerCase(); }
      else if (sort.key === 'description') { va = (a.description || '').toLowerCase(); vb = (b.description || '').toLowerCase(); }
      else if (sort.key === 'airPrice') { va = a.airPrice || 0; vb = b.airPrice || 0; }
      else if (sort.key === 'oceanPrice') { va = a.oceanPrice || 0; vb = b.oceanPrice || 0; }
      else if (sort.key === 'sellingPrice') { va = a.sellingPrice || 0; vb = b.sellingPrice || 0; }
      else { va = a[sort.key] || 0; vb = b[sort.key] || 0; }
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filterItems = (items) => {
    if (!plFilter.trim()) return items;
    const words = plFilter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return items.filter(item => {
      const text = `${item.sku || ''} ${item.displaySku || ''} ${item.description || ''}`.toLowerCase();
      return words.every(w => text.includes(w));
    });
  };

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;

  return (
    <div>
      <div className="sub-tabs">
        {TABS.map(tb => (
          <button key={tb} className={`sub-tab${tab === tb ? ' active' : ''}`} onClick={() => setTab(tb)}>
            {tb === 'orderPricing' ? 'Cellcom Pricing' : 'Price List'}
          </button>
        ))}
      </div>

      {/* ORDER PRICING TAB — removed, dead code cleaned */}
      {false && orderData && (
        <div>
          <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 24 }}>
            <div className="summary-card success">
              <div className="label">TOTAL INCOME (CELLCOM)</div>
              <div className="value">{fmt(orderData.totalIncome)}</div>
              <div className="sub">{orderData.orders?.length || 0} orders</div>
            </div>
            <div className="summary-card info">
              <div className="label">ORDERS WITH PRICING</div>
              <div className="value">{orderData.orders?.filter(o => o.items.some(i => i.hasSavedPrice)).length || 0} / {orderData.orders?.length || 0}</div>
              <div className="sub">orders priced</div>
            </div>
          </div>

          <h2 style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>
            CELLCOM ORDER PRICING
          </h2>

          {(!orderData.orders || orderData.orders.length === 0) ? (
            <div className="empty-state">No items allocated to Cellcom yet</div>
          ) : orderData.orders.map(order => {
            const isExpanded = expanded.has(order.orderNo);
            return (
              <div key={order.orderNo} className="order-group" style={{ marginBottom: 10 }}>
                <div className="order-group-header" onClick={() => toggle(order.orderNo)}
                  style={{ display: 'grid', gridTemplateColumns: '24px 120px 50px 50px 1fr auto', alignItems: 'center', gap: 10 }}>
                  <span className={`arrow${isExpanded ? ' open' : ''}`}>&#9654;</span>
                  <span className="order-no">{order.orderNo}</span>
                  <span className={`badge badge-${(order.origin || '').toLowerCase()}`}>{order.origin}</span>
                  <span className={`badge badge-${(order.docType || '').toLowerCase()}`}>{order.docType}</span>
                  <span className="text-muted" style={{ fontSize: 13 }}>{order.items.length} SKUs</span>
                  <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, color: order.totalIncome > 0 ? '#1a7a4a' : '#9ba3af' }}>
                    {order.totalIncome > 0 ? fmt(order.totalIncome) : '\u2014'}
                  </span>
                </div>
                {isExpanded && (
                  <div className="order-group-body">
                    <div className="table-wrap" style={{ marginTop: 0 }}>
                      <table>
                        <thead>
                          <tr>
                            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('sku')}>SKU{sortIcon('sku')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('description')}>Product{sortIcon('description')}</th>
                            <th className="text-end">Ship</th>
                            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => toggleSort('cellcomQty')}>Cellcom Qty{sortIcon('cellcomQty')}</th>
                            <th className="text-end">Unit Cost</th>
                            <th className="text-end" style={{ color: '#6c757d' }}>List Price</th>
                            <th className="text-end" style={{ background: '#fff9e6', borderBottom: '2px solid #C8A22C' }}>Selling Price</th>
                            <th className="text-end" style={{ fontWeight: 700 }}>Income</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortItems(order.items).map((item, i) => {
                            const autoListPrice = item.transportMode === 'AIR' ? item.airPrice : item.transportMode === 'SEA' ? item.oceanPrice : null;
                            return (
                              <tr key={i}>
                                <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{item.displaySku || item.sku}</td>
                                <td>{item.description}</td>
                                <td className="text-center">
                                  {item.transportMode ? (
                                    <span style={{
                                      fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                                      background: item.transportMode === 'AIR' ? '#e8f4fd' : '#e8f5e9',
                                      color: item.transportMode === 'AIR' ? '#1565c0' : '#2e7d32'
                                    }}>
                                      {item.transportMode === 'AIR' ? '\u2708 AIR' : '\u26F5 OCEAN'}
                                    </span>
                                  ) : <span className="text-muted">\u2014</span>}
                                </td>
                                <td className="text-end fw-bold">{fmtQty(item.cellcomQty)}</td>
                                <td className="text-end text-muted">{fmt(item.unitCost)}</td>
                                <td className="text-end text-muted">
                                  {autoListPrice != null ? fmt(autoListPrice) : (item.airPrice != null || item.oceanPrice != null) ?
                                    <span title={`Air: ${item.airPrice ?? '—'} / Ocean: ${item.oceanPrice ?? '—'}`} style={{ cursor: 'help' }}>
                                      {item.airPrice != null ? fmt(item.airPrice) : '—'} / {item.oceanPrice != null ? fmt(item.oceanPrice) : '—'}
                                    </span> : '\u2014'}
                                </td>
                                <td className="text-end" style={{ background: '#fff9e6' }}>
                                  {canEdit ? (
                                    <input className="inline-edit" type="number" step="0.01"
                                      key={`${item.sku}-${item.sellingPrice}`}
                                      defaultValue={item.sellingPrice != null ? item.sellingPrice : ''}
                                      placeholder={autoListPrice != null ? autoListPrice.toFixed(2) : '\u2014'}
                                      onBlur={e => {
                                        const val = e.target.value || (autoListPrice != null ? autoListPrice : null);
                                        if (val != null) saveOrderPrice(order.orderNo, item.sku, item.cellcomQty, val);
                                      }}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                          const val = e.target.value || (autoListPrice != null ? autoListPrice : null);
                                          if (val != null) saveOrderPrice(order.orderNo, item.sku, item.cellcomQty, val);
                                        }
                                      }}
                                      style={{ width: 80, textAlign: 'right' }} />
                                  ) : (
                                    <span className="fw-bold">{item.sellingPrice != null ? fmt(item.sellingPrice) : '\u2014'}</span>
                                  )}
                                </td>
                                <td className="text-end fw-bold" style={{ color: item.income > 0 ? '#1a7a4a' : '#9ba3af' }}>
                                  {item.income > 0 ? fmt(item.income) : '\u2014'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ borderTop: '2px solid #dee2e6' }}>
                            <td colSpan="7" className="text-end fw-bold">Order Income:</td>
                            <td className="text-end fw-bold" style={{ color: '#1a7a4a', fontSize: 15 }}>{fmt(order.totalIncome)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* PRICE LIST TAB */}
      {tab === 'priceList' && priceListData && (() => {
        const allItems = [...(priceListData.priced || []), ...(priceListData.unpriced || [])];
        const filtered = filterItems(allItems);
        const isEditing = canEdit && !locked;

        return (
          <div>
            <div className="flex-between" style={{ marginBottom: 16 }}>
              <h2 style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, letterSpacing: 1 }}>
                SKU PRICE LIST (CELLCOM)
              </h2>
              {canEdit && (
                <button style={{
                  padding: '8px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', border: 'none',
                  background: locked ? '#f1f5f9' : '#dc2626', color: locked ? '#64748b' : '#fff',
                }} onClick={() => setLocked(!locked)}>
                  {locked ? 'Locked' : 'Unlocked — edit & tab out to save'}
                </button>
              )}
            </div>

            {/* Filter + Group Prices */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <input className="input" placeholder="Filter: acton, stanmore, woburn..." value={plFilter}
                onChange={e => setPlFilter(e.target.value)} style={{ maxWidth: 300 }} />
              {plFilter.trim() && filtered.length > 0 && isEditing && (
                <>
                  <span className="text-muted" style={{ fontSize: 13 }}>{filtered.length} products</span>
                  <input className="input input-sm" type="number" step="0.01"
                    placeholder="Air price..." value={groupAirPrice}
                    onChange={e => setGroupAirPrice(e.target.value)} style={{ width: 110 }} />
                  <input className="input input-sm" type="number" step="0.01"
                    placeholder="Ocean price..." value={groupOceanPrice}
                    onChange={e => setGroupOceanPrice(e.target.value)} style={{ width: 110 }} />
                  <button className="btn btn-gold btn-sm"
                    disabled={!groupAirPrice && !groupOceanPrice}
                    onClick={() => saveGroupPrice(filtered)}>
                    Apply to {filtered.length}
                  </button>
                </>
              )}
            </div>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('sku')}>SKU{sortIcon('sku')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('description')}>Product{sortIcon('description')}</th>
                    <th className="text-end" style={{ background: '#e8f4fd', borderBottom: '2px solid #1565c0', cursor: 'pointer' }}
                      onClick={() => toggleSort('airPrice')}>✈️ Air Price{sortIcon('airPrice')}</th>
                    <th className="text-end" style={{ background: '#e8f5e9', borderBottom: '2px solid #2e7d32', cursor: 'pointer' }}
                      onClick={() => toggleSort('oceanPrice')}>🚢 Ocean Price{sortIcon('oceanPrice')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortItems(filtered).map((item) => (
                    <tr key={item.sku}>
                      <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{item.displaySku || item.sku}</td>
                      <td>{item.description}</td>
                      <td className="text-end" style={{ background: '#f0f7ff' }}>
                        {isEditing ? (
                          <input className="inline-edit" type="text" inputMode="decimal"
                            key={`${item.sku}-air-${item.airPrice}-${locked}`}
                            defaultValue={item.airPrice != null ? item.airPrice : ''}
                            placeholder="—"
                            onBlur={e => { const v = e.target.value.trim(); if (v === '' && item.airPrice != null) { api.put('/pricing/sku-price', { sku: item.sku, airPrice: null }).then(() => fetchTab(true)); } else if (v && parseFloat(v) !== item.airPrice) saveSkuPrice(item.sku, 'air', v); }}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                            style={{ width: 100, textAlign: 'right' }} />
                        ) : (
                          <span className="fw-bold">{item.airPrice != null ? fmt(item.airPrice) : '\u2014'}</span>
                        )}
                      </td>
                      <td className="text-end" style={{ background: '#f0fff0' }}>
                        {isEditing ? (
                          <input className="inline-edit" type="text" inputMode="decimal"
                            key={`${item.sku}-ocean-${item.oceanPrice}-${locked}`}
                            defaultValue={item.oceanPrice != null ? item.oceanPrice : ''}
                            placeholder="—"
                            onBlur={e => { const v = e.target.value.trim(); if (v === '' && item.oceanPrice != null) { api.put('/pricing/sku-price', { sku: item.sku, oceanPrice: null }).then(() => fetchTab(true)); } else if (v && parseFloat(v) !== item.oceanPrice) saveSkuPrice(item.sku, 'ocean', v); }}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                            style={{ width: 100, textAlign: 'right' }} />
                        ) : (
                          <span className="fw-bold">{item.oceanPrice != null ? fmt(item.oceanPrice) : '\u2014'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
